import {
  DatePipe,
  DecimalPipe,
  NgClass,
  NgIf,
  NgTemplateOutlet,
} from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { AnalyticsService } from '@core/services/analytics.service';
import { AuctionDownloadedService } from '@core/services/auction-downloaded.service';
import { AuctionService } from '@core/services/auction.service';
import { AuthService } from '@core/services/auth.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faCalendarAlt,
  faHeart as faHeartRegular,
} from '@fortawesome/free-regular-svg-icons';
import {
  faFlag,
  faHeart,
  faHeart as faHeartSolid,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateModule } from '@ngx-translate/core';
import { BidService } from '@shared/services/bid.service';
import { LanguageService } from '@shared/services/language.service';
import { PlatformService } from '@shared/services/platform-service.service';
import { Subscription, interval } from 'rxjs';
import { AuctionView } from 'src/app/articles-manager/interfaces/auction.interface';
import { ArticleService } from 'src/app/articles-manager/services/article.service';
import { SellerBrandingService } from 'src/app/seller/services/seller-branding.service';

const memorabidAuctionTypeDescription = 'Memorabid';

@Component({
  selector: 'app-bid-element',
  templateUrl: './bid-element.component.html',
  styleUrls: ['./bid-element.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    FontAwesomeModule,
    RouterModule,
    NgClass,
    NgTemplateOutlet,
    NgIf,
    DecimalPipe,
  ],
  host: {
    ngSkipHydration: 'true',
  },
})
@UntilDestroy()
export class BidElementComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() idEvent: number = -1;
  slug = input<string | null>(null);
  auction = input<AuctionView>();
  @Input() set isHomepageEvidence(isEvidence: boolean) {
    this._isHomepageEvidence = isEvidence;
    this.setHostClassList(isEvidence);
  }
  get isHomepageEvidence(): boolean {
    return this._isHomepageEvidence;
  }
  private _isHomepageEvidence: boolean = null;

  faHeartRegular = faHeartRegular;
  faHeart = faHeart;
  faHeartSolid = faHeartSolid;
  faFlag = faFlag;
  faCalendarAlt = faCalendarAlt;
  faStar = faStar;

  isMemorabid: boolean = false;
  subscription: Subscription;
  millisecondsInASecond = 1000;
  hoursInADay = 24;
  minutesInAHour = 60;
  secondsInAMinute = 60;
  dateNow = new Date();
  timeDifference = signal<number>(0);
  language: string;
  imageWidth: number = 200;

  isBranded = computed(() => {
    return this.brandingService.isBranded() && !this.isHomepageEvidence;
  });

  brandingService = inject(SellerBrandingService);

  public timeDifferenceFromStart: number;
  public secondsToEnd: number;
  public minutesToEnd: number;
  public hoursToEnd: number;
  public daysToEnd: number;

  @ViewChild('imageShowcase') imageDiv: ElementRef;
  isStarred = signal(false);
  underReserve = computed(() => {
    if (!this.auction()) {
      return false;
    }

    if (this.auction().hasReservePrice === false) {
      return false;
    }

    // 48 hours after the auction end date
    if (
      -this.timeDifference() > 1000 * 60 * 60 * 24 * 2 ||
      -this.timeDifference() < 0
    ) {
      return false;
    }

    return !this.auction().reservePriceReached;
  });

  showCoverReserveButton = computed(() => {
    return (
      this.slug() == null &&
      this.underReserve() &&
      this.auction()?.isUserBestBider &&
      this.authService.isAuthenticated()
    );
  });

  showProposal = computed(() => {
    const userWinner =
      this.auction().isUserBestBider && this.auction().reservePriceReached;
    if (
      !userWinner &&
      !this.underReserve() &&
      this.auction()?.isFinished &&
      !this.auction()?.buyItNow &&
      this.slug() == null
    ) {
      return true;
    }
    return false;
  });

  private readonly articleService = inject(ArticleService);
  private readonly bidService = inject(BidService);
  public readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly datePipe = inject(DatePipe);
  private readonly _elRef = inject(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly auctionDownloadedService = inject(AuctionDownloadedService);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly _languageService = inject(LanguageService);
  private readonly platformService = inject(PlatformService);
  private readonly auctionService = inject(AuctionService);

  constructor() {
    this.language = this._languageService.userSelectedLanguage.code;
  }

  ngOnInit(): void {
    this.isMemorabid =
      this.auction().auctionTypeDescription === memorabidAuctionTypeDescription;
    this.updateTimeDifference();
    this.platformService.runOnBrowser(() => {
      this.subscription = interval(1000).subscribe(() =>
        this.updateTimeDifference()
      );
      this.imageWidth =
        Math.ceil((this.imageDiv?.nativeElement?.offsetWidth ?? 200) / 50) * 50;
    });
    this.platformService.runOnServer(() => {
      this.imageWidth = this.isHomepageEvidence ? 650 : 200;
    });

    this.articleService
      .isFavorite(this.auction().idArticle)
      .pipe(untilDestroyed(this))
      .subscribe((isFavorite) => {
        this.isStarred.set(isFavorite);
      });
  }

  ngAfterViewInit() {
    this.platformService.runOnBrowser(() => {
      this.imageWidth =
        Math.ceil((this.imageDiv?.nativeElement?.offsetWidth ?? 200) / 50) * 50;
    });
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  updateTimeDifference() {
    const startDate = new Date(
      this.datePipe.transform(
        this.auction().startDateView,
        'MM/dd/yyyy HH:mm'
      ) + ' UTC'
    );
    this.timeDifferenceFromStart = new Date().getTime() - startDate.getTime();
    this.getTimeDifference(
      this.timeDifferenceFromStart > 0
        ? this.auction().endDateView
        : this.auction().startDateView
    );
  }

  getTimeDifference(date: string) {
    const newDate = new Date(
      this.datePipe.transform(date, 'MM/dd/yyyy HH:mm') + ' UTC'
    );
    this.timeDifference.set(newDate.getTime() - new Date().getTime());
    this.secondsToEnd = Math.floor(
      (this.timeDifference() / this.millisecondsInASecond) %
        this.secondsInAMinute
    );
    this.minutesToEnd = Math.floor(
      (this.timeDifference() /
        (this.millisecondsInASecond * this.minutesInAHour)) %
        this.secondsInAMinute
    );
    this.hoursToEnd = Math.floor(
      (this.timeDifference() /
        (this.millisecondsInASecond *
          this.minutesInAHour *
          this.secondsInAMinute)) %
        this.hoursInADay
    );
    this.daysToEnd = Math.floor(
      this.timeDifference() /
        (this.millisecondsInASecond *
          this.minutesInAHour *
          this.secondsInAMinute *
          this.hoursInADay)
    );
  }

  goToArticle() {
    const backScrollPaths = [
      '/search-result-all',
      '/search-result-worn',
      '/search-result-store',
      '/search-result-signed',
      '/search-result',
      '/eventnow',
      '/home',
      '/tags',
      '/seller',
    ];

    const toScroll = backScrollPaths.some((path) =>
      this.router.url.includes(path)
    );

    this.auctionDownloadedService.scrollPosition = toScroll
      ? window.scrollY
      : 0;
    if (toScroll) {
      this.auctionDownloadedService.auctionSource =
        this.router.url.split('/')[2];
    }
    this.analyticsService.trackViewItem(this.auction());

    // Preload full auction detail in background for faster article page load
    const idAuction = this.auction()?.idAuction;
    if (idAuction) {
      // Avoid duplicate request if already cached
      if (!this.auctionDownloadedService.getPreloadedAuction(idAuction)) {
        this.auctionService
          .getByPk(idAuction)
          .pipe(untilDestroyed(this))
          .subscribe({
            next: (fullAuction) => {
              if (fullAuction) {
                // Store in cache for detail component to pick up
                // Cast to AuctionView shape if needed (detail returns Auction)
                // Convert minimal needed fields to AuctionView shape if response is Auction (has superset of fields)
                const toCache: AuctionView = {
                  ...(fullAuction as unknown as AuctionView),
                };
                this.auctionDownloadedService.cacheAuctionDetail(toCache);
              }
            },
            error: () => {
              // Silently ignore preload errors; navigation still proceeds
            },
          });
      }
    }
  }

  onToggleWishlist(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.auction()?.idArticle) {
      const starredSub = this.isStarred()
        ? this.articleService.delete({ idArticle: this.auction().idArticle })
        : this.articleService.save({ idArticle: this.auction().idArticle });
      starredSub.subscribe();
    }
  }

  openProposalPopup(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.auction()?.idAuction) {
      this.bidService.makeOfferMessage(this.auction().idAuction);
    }
  }

  openCoverReservePopup(event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.auction()?.idAuction) {
      this.bidService.makeBidReservePrice(this.auction().idAuction);
    }
  }

  private setHostClassList(isEvidence: boolean): void {
    if (isEvidence && this._elRef?.nativeElement) {
      this._renderer.addClass(this._elRef.nativeElement, 'grid-evidence');
    }
  }
}
